import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FlatList, StatusBar, VirtualizedList } from "react-native";
import {
  getNewMessageNotifications,
  getNotificationHasResult,
  getNotificationIsLoading,
  getNotificationIsRequesting,
  getNotificationPage,
  getNotifications,
} from "@microdoing/shared/services/notification/selectors";
import {
  getNotificationsRequest,
  loadNotificationsRequest,
} from "@microdoing/shared/services/notification/actions";
import i18n from "@microdoing/shared/i18n";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import MessageIcon from "app/scenes/UserTracksScreen/MessageIcon";
import { Box, EmptyJouvie, MainText, MainTitle } from "app/components/common";
import HeaderFullScreen from "app/components/common/HeaderFullScreen";
import theme from "app/Theme/Theme";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import { getScrollTop } from "@microdoing/shared/services/nav/selectors";
import { changeNavField } from "@microdoing/shared/services/nav/actions";
import { useNavigation } from "@react-navigation/native";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import NotificationRow from "./NotificationRow/NotificationRow";
import { breadCrumbsNotificationScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import { getReviewsRequest } from "@microdoing/shared/services/userCampaign/actions";
import HeaderTabBar from "app/navigation/TabBar/HeaderTabBar";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const Notification = () => {
  const flatListRef = useRef<VirtualizedList<any>>();

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const userId = useSelector(getUserId);
  const isNotificationRequesting =
    useSelector(getNotificationIsRequesting) || false;
  const isNotificationLoading = useSelector(getNotificationIsLoading);
  const notificationHasResult = useSelector(getNotificationHasResult);
  const notificationPage = useSelector(getNotificationPage);
  const notifications = useSelector(getNotifications);
  const language = useSelector(getLanguage);
  const shouldScrollTop = useSelector(getScrollTop);

  const insets = useSafeAreaInsets();

  useEffect(() => {
    dispatch(getNotificationsRequest({ page: 0, userId: userId }));
    dispatch(getReviewsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (shouldScrollTop) {
      dispatch(changeNavField({ scrollTop: false }));
      flatListRef?.current?.scrollToOffset({ animated: true, offset: 0 });
    }
  }, [shouldScrollTop, flatListRef]);

  const keyExtractor = (item, index) => {
    return index.toString();
  };

  const loadMore = () => {
    if (
      !isNotificationRequesting &&
      !isNotificationLoading &&
      notificationHasResult
    ) {
      dispatch(
        loadNotificationsRequest({
          userId: userId,
          page: notificationPage,
        }),
      );
    }
  };

  const handleRefresh = () => {
    dispatch(getNotificationsRequest({ page: 0, userId: userId }));
  };

  const onPressMessage = () => {
    navigation.navigate("Message");
  };

  const renderNotification = notification => {
    return <NotificationRow notification={notification.item} />;
  };

  if (isWebDesktop) {
    return (
      <WebDesktopView breadcrumbsPath={breadCrumbsNotificationScreen}>
        {notifications.length < 1 ? (
          <EmptyJouvie
            firstMessage={i18n.t("no-notifications")}
            secondMessage={i18n.t("go-challenges")}
            onPress={() => navigation.navigate("Home")}
          />
        ) : (
          <FlatList
            ref={flatListRef}
            data={notifications}
            extraData={language}
            renderItem={renderNotification}
            keyExtractor={keyExtractor}
            keyboardShouldPersistTaps="always"
            onEndReached={loadMore}
            refreshing={isNotificationRequesting}
            onRefresh={handleRefresh}
            onEndReachedThreshold={0}
            contentContainerStyle={{
              backgroundColor: "white",
              paddingBottom: isWebDesktop ? theme.spacing.xm : 0,
              paddingTop: isWebDesktop ? theme.spacing.s : 0,
            }}
            style={{
              borderRadius: 8,
            }}
          />
        )}
      </WebDesktopView>
    );
  }

  return (
    <Box
      style={{
        backgroundColor: theme.colors["neutral.white"],
        flex: 1,
        paddingTop: insets.top,
      }}
      testID={"notification-screen"}
    >
      <StatusBar backgroundColor={theme.colors["neutral.white"]} />
      <Box paddingHorizontal="sm" backgroundColor="neutral.white">
        <HeaderTabBar
          title={
            <MainTitle variant="l8-bold">
              {i18n.t("last-notifications")}
            </MainTitle>
          }
        />
      </Box>
      {notifications.length < 1 ? (
        <EmptyJouvie
          firstMessage={i18n.t("no-notifications")}
          secondMessage={i18n.t("go-challenges")}
          onPress={() => navigation.navigate("Home")}
        />
      ) : (
        <FlatList
          ref={flatListRef}
          data={notifications}
          extraData={language}
          renderItem={renderNotification}
          keyExtractor={keyExtractor}
          keyboardShouldPersistTaps="always"
          onEndReached={loadMore}
          showsVerticalScrollIndicator={false}
          refreshing={isNotificationRequesting}
          onRefresh={handleRefresh}
          onEndReachedThreshold={0}
        />
      )}
    </Box>
  );
};

export default Notification;
